// src/components/aiTalentDrive/ReceiverField.tsx
// Backdrop of the hero on /ai-talent-drive/hiring-companies: the drive page's
// signal run backwards. There, one voice fans out to 50+ companies; here the
// hairlines come in from the right edge, converge, and land as one line
// under the headcount. Decorative only. Plain canvas, no dependency; the
// cursor leans on the amplitude, scrolling the hero away lets the lines go.
//
// The converge point is read off the element marked data-receiver-anchor
// (the big numeral), so the line meets it wherever the layout puts it.
import { useEffect, useRef } from "react";

const smooth = (v: number) => {
  const t = Math.min(1, Math.max(0, v));
  return t * t * (3 - 2 * t);
};

export default function ReceiverField() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const host = canvas?.parentElement;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !host || !ctx) return;

    const still = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const colour = getComputedStyle(canvas).color; // the page's lime token
    let w = 0;
    let h = 0;
    let fx = 0;
    let fy = 0;
    let raf = 0;
    let frame = 0;
    let onScreen = true;
    const mouse = { y: 0.5, ty: 0.5 };

    const anchor = () => {
      const a = host.querySelector<HTMLElement>("[data-receiver-anchor]")?.getBoundingClientRect();
      const r = host.getBoundingClientRect();
      // just under the numeral, ending a little past its right edge
      fx = a ? a.right - r.left + 28 : w * 0.55;
      // no numeral (loading failed): run the line just above the button row instead
      const cta = host.querySelector<HTMLElement>(".cs-hero__cta")?.getBoundingClientRect();
      fy = a ? a.bottom - r.top + Math.max(18, a.height * 0.2) : cta ? cta.top - r.top - 24 : h * 0.78;
    };

    const resize = () => {
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      const r = host.getBoundingClientRect();
      w = r.width;
      h = r.height;
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      anchor();
    };

    const draw = (ms: number) => {
      const t = still ? 0 : ms / 1000;
      mouse.y += (mouse.ty - mouse.y) * 0.06;
      if (frame++ % 30 === 0) anchor(); // the numeral grows as the count lands

      const rect = host.getBoundingClientRect();
      const gone = smooth(-rect.top / Math.max(1, rect.height)); // 0 at the top, 1 once scrolled away
      const small = w < 700;
      const lines = small ? 20 : 44;
      const span = Math.max(1, w - fx);
      // the cursor's height lifts the amplitude of the whole bundle
      const energy = 0.45 + 0.55 * (1 - Math.min(1, Math.abs(mouse.y * h - fy) / (h * 0.7)));

      ctx.clearRect(0, 0, w, h);
      ctx.strokeStyle = colour;
      ctx.fillStyle = colour;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";

      // 1. the many: from the right edge, converging on the focus
      ctx.lineWidth = 1;
      for (let i = 0; i < lines; i++) {
        const k = i / (lines - 1);
        const yEdge = h * (0.03 + 0.94 * k);
        const mid = 1 - Math.abs(k * 2 - 1); // brightest through the middle
        ctx.beginPath();
        for (let x = fx; x <= w + 8; x += 8) {
          const e = smooth((x - fx) / span);
          const wave = Math.sin(x * 0.011 - t * 1.5 + i * 0.55) * (4 + 15 * energy) * e * (1 - gone * 0.85);
          const y = fy + (yEdge - fy) * Math.pow(e, 1.3) + wave;
          if (x === fx) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.globalAlpha = (0.08 + 0.26 * mid) * (1 - gone);
        ctx.stroke();
      }

      // 2. the one: a voice, left of the focus, under the headcount
      ctx.lineWidth = 2.5;
      ctx.globalAlpha = 0.95 * (1 - gone);
      ctx.beginPath();
      for (let x = 0; x <= fx; x += 4) {
        const env = smooth(x / Math.max(1, fx)) * (0.55 + 0.45 * Math.sin(t * 1.3 + x * 0.004));
        const y = fy + (Math.sin(x * 0.045 - t * 5) * 8 + Math.sin(x * 0.11 + t * 3) * 3.5) * env * energy;
        if (x === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(fx, fy, 5, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = 1;

      if (!still && onScreen) raf = requestAnimationFrame(draw);
    };

    const start = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(draw);
    };

    const onMove = (e: PointerEvent) => {
      if (e.pointerType !== "mouse") return;
      const r = host.getBoundingClientRect();
      mouse.ty = (e.clientY - r.top) / Math.max(1, r.height);
    };

    resize();
    start();
    const ro = new ResizeObserver(() => { resize(); if (still) start(); });
    ro.observe(host);
    const io = new IntersectionObserver(([entry]) => {
      onScreen = entry.isIntersecting;
      if (onScreen && !still) start();
    });
    io.observe(host);
    host.addEventListener("pointermove", onMove);
    // Still mode draws one frame: redraw when the numeral arrives with the data
    // (nothing counts under reduced motion, so this stays quiet).
    const mo = still ? new MutationObserver(() => { resize(); start(); }) : null;
    mo?.observe(host, { childList: true, subtree: true });
    // fonts change the numeral's width after first paint
    document.fonts?.ready.then(() => { resize(); if (still) start(); });
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      io.disconnect();
      mo?.disconnect();
      host.removeEventListener("pointermove", onMove);
    };
  }, []);

  return <canvas ref={canvasRef} className="cs-field" aria-hidden="true" />;
}
